import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  HomeIcon,
  MapPinIcon,
  PhotoIcon,
  NewspaperIcon,
  ClipboardDocumentListIcon,
  QuestionMarkCircleIcon,
  StarIcon,
  UserCircleIcon,
  XMarkIcon,
  ArrowLeftOnRectangleIcon,
} from '@heroicons/react/24/outline';

import { useAuth } from '../../contexts/AuthContext'; // Make sure this path is correct

const navItems = [
  { name: 'Dashboard', path: '/dashboard', icon: HomeIcon },
  { name: 'Destinations', path: '/locations', icon: MapPinIcon },
  { name: 'Media Assets', path: '/media', icon: PhotoIcon },
  { name: 'Packages', path: '/packages', icon: NewspaperIcon },
  { name: 'Include & Exclude', path: '/pkg-inclusion-exclusion', icon: ClipboardDocumentListIcon },
  { name: 'FAQs', path: '/faqs', icon: QuestionMarkCircleIcon },
  { name: 'Testimonials', path: '/testimonials', icon: StarIcon },
  { name: 'Profile', path: '/profile', icon: UserCircleIcon },
];

const NavItem = ({ item, isActive, toggleSidebar }) => (
  <li key={item.name} className="mb-2">
    <Link
      to={item.path}
      onClick={toggleSidebar}
      className={`flex items-center p-3 rounded-xl transition-all duration-300 group relative overflow-hidden ${
        isActive
          ? 'bg-gradient-to-r from-indigo-500 to-blue-600 text-white font-semibold shadow-lg transform scale-[1.02]'
          : 'text-gray-700 hover:bg-gradient-to-r hover:from-gray-50 hover:to-indigo-50 hover:text-indigo-700 hover:shadow-md hover:transform hover:scale-[1.01]'
      }`}
    >
      {isActive && (
        <div className="absolute inset-0 bg-gradient-to-r from-indigo-600 to-blue-700 opacity-10 rounded-xl"></div>
      )}
      <item.icon
        className={`h-5 w-5 mr-3 transition-all duration-300 relative z-10 ${
          isActive
            ? 'text-white drop-shadow-sm'
            : 'text-gray-500 group-hover:text-indigo-600 group-hover:scale-110'
        }`}
      />
      <span className="text-sm font-medium relative z-10">{item.name}</span>
      {isActive && (
        <div className="ml-auto">
          <div className="w-2 h-2 bg-white rounded-full shadow-sm"></div>
        </div>
      )}
    </Link>
  </li>
);

function Sidebar({ isOpen, toggleSidebar }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth(); // Use AuthContext

  const handleLogoutClick = () => {
    logout(); // Log the user out
    toggleSidebar(); // Close sidebar on mobile
    navigate('/login'); // Redirect to login
  };

  return (
    <aside
      className={`
        flex-shrink-0 flex flex-col 
        w-56 md:w-[240px] lg:w-[260px] p-4 lg:p-5 
        bg-gradient-to-b from-white via-gray-50 to-indigo-50 backdrop-blur-lg border-r-2 border-indigo-100
        transform transition-all duration-300 ease-in-out
        ${isOpen ? 'translate-x-0 fixed top-0 left-0 h-full z-50 rounded-r-2xl shadow-2xl' : '-translate-x-full fixed top-0 left-0 h-full z-50 rounded-r-2xl shadow-2xl'} 
        md:translate-x-0 md:static md:h-screen md:overflow-y-auto md:shadow-xl md:rounded-r-none
      `}
    >
      {/* Sidebar Header */}
      <div className="flex justify-between items-center mb-6 pb-4 border-b-2 border-indigo-200 pr-2">
        <img
          src="/tour-kingdom.png"
          alt="TourKingdom Logo"
          className="w-48 max-w-full object-contain shadow-md hover:shadow-lg transition-shadow duration-300"
        />
        <button
          onClick={toggleSidebar}
          className="md:hidden text-gray-500 hover:text-indigo-600 p-2 rounded-xl hover:bg-indigo-50 transition-all duration-200 shadow-sm hover:shadow-md ml-2 flex-shrink-0"
        >
          <XMarkIcon className="h-6 w-6" />
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 space-y-2">
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-200 shadow-sm">
          <div className="flex items-center mb-3">
            <div className="w-3 h-3 bg-blue-500 rounded-full mr-3"></div>
            <p className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Main Menu</p>
          </div>
          <ul className="space-y-1">
            {navItems.map((item) => (
              <NavItem
                key={item.name}
                item={item}
                isActive={
                  location.pathname === item.path ||
                  (location.pathname.startsWith(item.path + '/') && item.path !== '/')
                }
                toggleSidebar={toggleSidebar}
              />
            ))}
          </ul>
        </div>

        {/* Settings and Logout */}
        <div className="mt-auto pt-4">
          <div className="bg-gradient-to-r from-red-50 to-pink-50 rounded-xl p-4 border border-red-200 shadow-sm">
            <div className="flex items-center mb-3">
              <div className="w-3 h-3 bg-red-500 rounded-full mr-3"></div>
              <p className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Account</p>
            </div>
            <ul>
              <li>
                <button
                  onClick={handleLogoutClick}
                  className="flex items-center p-3 rounded-xl text-gray-700 hover:bg-gradient-to-r hover:from-red-100 hover:to-pink-100 hover:text-red-700 group w-full text-left transition-all duration-300 hover:shadow-md hover:transform hover:scale-[1.01]"
                >
                  <ArrowLeftOnRectangleIcon className="h-5 w-5 mr-3 text-gray-500 group-hover:text-red-600 group-hover:scale-110 transition-all duration-300" />
                  <span className="text-sm font-medium">Log Out</span>
                </button>
              </li>
            </ul>
          </div>
        </div>
      </nav>
    </aside>
  );
}

export default Sidebar;
